/* eslint-disable @typescript-eslint/no-explicit-any */
import { useSelector } from 'react-redux'
import raceApi from 'apis/raceApi'
import { constants } from 'apps'
import { RaceTable, SwitchBtn } from 'features/Race/components'
import { useAppDispatch, useAppSelector, useDebounce, useToggle, useUpdateEffect } from 'hooks'
import { NOTIFICATION_MESSAGE } from 'i18n/constants'
import {
  GetRaceListParams,
  GetRaceListPopupParams,
  RaceClassNumber,
  RaceFieldType,
  RaceFilters,
  RaceOption,
  RaceStatus,
  RecordRace,
  RegionFilters,
  RegionOption,
  RegionOptionFilter
} from 'models'
import { ChangeEvent, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { OneLineTitle, SearchInput } from 'shared'
import Select from 'shared/Select'
import { WS_MANAGER } from 'socket/socketClient'
import { openListColumns } from 'utils/columns'
import { handleAsyncRequest, shortenRaceCourseName } from 'utils/helper'
import { useNavigate, useSearchParams } from 'react-router-dom'
import _ from 'lodash'
import { convertFullDateLocal } from 'utils/time'
import { NUMBER, STRING_REGION, STRING_STATUS } from 'apps/constants'
import OpenStyled from './styled'
import { getTooltipInfo } from 'apps/redux/tooltip/action'
import { weatherSelector } from 'apps/redux/weather/selector'

export type RaceClassFilter = {
  name: RaceClassNumber | 'All'
  isActive: boolean
}
export type RaceTypeFilter = {
  name: RaceFieldType | 'All'
  isActive: boolean
}

export type ClassFilters = RaceClassFilter[]
export type TypeFilters = RaceTypeFilter[]

const defaultParams: GetRaceListParams = {
  limit: NUMBER.TWENTY,
  page: NUMBER.ONE,
  status: RaceStatus.OPEN,
  freeRace: false,
  myHorse: false,
  search: ''
}

const date = new Date()
const paramsFilterResult: GetRaceListPopupParams = {
  limit: NUMBER.TWENTY,
  page: NUMBER.ONE,
  startAt: convertFullDateLocal(date),
  endAt: convertFullDateLocal(date),
  startInstance: '1000',
  endInstance: '1000',
  raceClass: RaceClassNumber.Class1
  // sort: ['id-desc']
}

let isRefresh = false

const defaultTypeFilters: TypeFilters = [
  {
    name: 'All',
    isActive: true
  },
  {
    name: RaceFieldType.Turf,
    isActive: false
  },
  {
    name: RaceFieldType.Dirt,
    isActive: false
  }
]
const defaultClassFilters: ClassFilters = [
  {
    name: 'All',
    isActive: true
  },
  {
    name: RaceClassNumber.Class1,
    isActive: false
  },
  {
    name: RaceClassNumber.Class2,
    isActive: false
  },
  {
    name: RaceClassNumber.Class3,
    isActive: false
  },
  {
    name: RaceClassNumber.Class4,
    isActive: false
  },
  {
    name: RaceClassNumber.Class5,
    isActive: false
  },
  {
    name: RaceClassNumber.Class6,
    isActive: false
  },
  {
    name: RaceClassNumber.Class7,
    isActive: false
  },
  {
    name: RaceClassNumber.ClassFreeStyle,
    isActive: false
  }
]
const defaultRegionFilters: RegionFilters = [
  {
    name: 'All',
    isActive: true
  },
  {
    name: RegionOption.TOKYO,
    isActive: false
  },
  {
    name: RegionOption.SEOUL,
    isActive: false
  }
]

const defaultRaceFilters: RaceFilters[] = [
  {
    name: 'Default',
    isActive: true
  },
  {
    name: RaceOption.NORMAL_RACE,
    isActive: false
  },
  {
    name: RaceOption.PRIVATE_RACE,
    isActive: false
  }
]

function Open() {
  // const getEnableWeatherSelector = useSelector(enableWeatherSelector)
  const getWeatherSelector = useSelector(weatherSelector)
  const [searchParams, setSearchParams] = useSearchParams()
  const dispatch = useAppDispatch()
  const [races, setRaces] = useState<RecordRace[]>([])
  const paramsSearch: any= Object.fromEntries(searchParams.entries())
  const [isFreeOn, toggleIsFreeOn] = useToggle(JSON.parse(paramsSearch?.freeRace || "false"))
  const [params, setParams] = useState<GetRaceListParams>(_.isEmpty(paramsSearch) ? defaultParams : _.isEmpty(races) ? {...paramsSearch, page: 1,} : paramsSearch)
  const [classFilters, setClassFilters] = useState<ClassFilters>(defaultClassFilters)
  const [typeFilters, setTypeFilters] = useState<TypeFilters>(defaultTypeFilters)
  const [regionFilters, setRegionFilters] = useState<RegionFilters>(defaultRegionFilters)
  const [raceFilters, setRaceFilters] = useState<RaceFilters[]>(defaultRaceFilters)
  const [searchValue, setSearchValue] = useState<string>(paramsSearch?.search || '')
  const debounceSearchValue = useDebounce<string>(searchValue, constants.DEBOUNCE_TIME_RACE)
  const [loader, setLoader] = useState(true)
  const [classSelect, setClassSelect] = useState(paramsSearch?.raceClass || 'Class')
  const [typeSelect, setTypeSelect] = useState(paramsSearch?.fieldType || 'Field type')
  const [regionSelect, setRegionSelect] = useState(paramsSearch?.region || 'Region')
  const [raceSelect, setRaceSelect] = useState(paramsSearch?.race || 'Default')
  const [totalRace, setTotalRace] = useState(NUMBER.ZERO)
  const memoizedOpenListColumns = useMemo(() => openListColumns, [])
  const auth = useAppSelector(state => state.auth)
  const [isMyHorseOn, toggleIsMyHorseOn] = useToggle(JSON.parse(paramsSearch?.myHorse || "false"))
  const useRefSwitch = useRef<HTMLButtonElement>(null)
  const { t } = useTranslation()
  // const refSwitch = useRefSwitch.current && useRefSwitch.current?.className
  // const isRefSwitch = refSwitch?.includes('switch-btn--on')
  const navigate = useNavigate()
  const statusRef = useRef(false)
  const tmpGetWeatherSelectorRef = useRef<any>(null)
  const [isFetching, setIsFetching] = useState(false)

  useUpdateEffect(() => {
    setRaces([])
    setParams({ ...params, myHorse: isMyHorseOn, page: NUMBER.ONE })
  }, [isMyHorseOn])

  useLayoutEffect(() => {
    const handleLoadingRaces = () => {
      if (races.length < NUMBER.TWENTY && races.length > NUMBER.ZERO) {
        setLoader(true)
      }
    }

    handleLoadingRaces()
  }, [])

  useEffect(() => {
    dispatch(getTooltipInfo())
  }, [])
  
  const fetchListRaces = async (): Promise<void> => {
    setLoader(false)
    setIsFetching(true)
    const [error, result] = await handleAsyncRequest(raceApi.getRaceList(params))
    if ((error as any)?.code === 429 && (error as any)?.message === 'Too Many Requests') {
      await new Promise(resolve => setTimeout(resolve, 1000))
      return fetchListRaces()
    }
    const records = result?.data.records
    setTotalRace(result?.data?.total || NUMBER.ZERO)
    if (records && records.length > NUMBER.ZERO) {
      if (isRefresh) {
        setRaces([...records])
      } else {
        const newRace = [
          ...new Map(
            [...races, ...records].map((item: any) => [`${item?.id}`, item])
          ).values(),
        ]
        setRaces(newRace)
      }
    }
    if (result?.data?.total === NUMBER.ZERO) {
      setRaces([])
    }
    if (
      params.page !== NUMBER.ONE &&
      records &&
      (clonedRates.length < records.length) &&
      result?.data?.total > NUMBER.TWENTY
    ) {
      setParams({ ...params, page: params.page + NUMBER.ONE })
      statusRef.current = true
    }
    setLoader(true)
    isRefresh = false
    setIsFetching(false)
  }

  useEffect(() => {
    fetchListRaces()
    navigate(
      `/race/open${renderQueries(params as GetRaceListParams)}`,
      {
        replace: true,
      }
    );
    
    setSearchParams(params as any)
    const subscription = WS_MANAGER.subscribe('/topic/race-status', handleSocketChange)
    return () => {
      subscription?.then(sub => sub?.unsubscribe())
    }
  }, [params])

  useUpdateEffect(() => {
    setRaces([])
    setParams({ ...params, search: debounceSearchValue?.trim(), page: NUMBER.ONE })
  }, [debounceSearchValue])

  useUpdateEffect(() => {
    setRaces([])
    setParams({ ...params, freeRace: isFreeOn, page: NUMBER.ONE })
  }, [isFreeOn])

  const updateClassFilters = useCallback((classFilters: ClassFilters, clickedFilter: RaceClassFilter) => {
    const clonedClassFilters = [...classFilters]

    clonedClassFilters.forEach(filter => {
      if (filter.name === clickedFilter.name) {
        filter.isActive = true
        setClassSelect(filter.name)
      } else {
        filter.isActive = false
      }
    })

    return clonedClassFilters
  }, [])

  const updateTypeFilters = useCallback((typeFilters: TypeFilters, clickedFilter: RaceTypeFilter) => {
    const clonedTypeFilters = [...typeFilters]

    clonedTypeFilters.forEach(filter => {
      if (filter.name === clickedFilter.name) {
        filter.isActive = true
        setTypeSelect(filter.name)
      } else {
        filter.isActive = false
      }
    })

    return clonedTypeFilters
  }, [])

  const updateRegionFilters = useCallback((regionFilters: RegionFilters, clickedFilter: RegionOptionFilter) => {
    const clonedRegionFilters = [...regionFilters]

    clonedRegionFilters.forEach(filter => {
      if (filter.name === clickedFilter.name) {
        filter.isActive = true
        setRegionSelect(filter.name)
      } else {
        filter.isActive = false
      }
    })

    return clonedRegionFilters
  }, [])

  const updateRaceFilters = useCallback((raceFilters: RaceFilters[], clickedFilter: RaceFilters) => {
    const clonedRegionFilters = [...raceFilters]

    clonedRegionFilters.forEach(filter => {
      if (filter.name === clickedFilter.name) {
        filter.isActive = true
        setRaceSelect(filter.name)
      } else {
        filter.isActive = false
      }
    })

    return clonedRegionFilters
  }, [])

  const updateParamsAfterFilterClicked = useCallback((params: GetRaceListParams, clickedFilter: RaceClassFilter) => {
    const clonedParams = { ...params, page: 1 }

    if (clickedFilter.name !== 'All') {
      clonedParams.raceClass = clickedFilter.name
    } else {
      delete clonedParams.raceClass
    }

    return clonedParams
  }, [])

  const updateParamsAfterTypeClicked = useCallback((params: GetRaceListParams, clickedFilter: RaceTypeFilter) => {
    const clonedParams = { ...params, page: 1 }

    if (clickedFilter.name !== 'All') {
      clonedParams.fieldType = clickedFilter.name
    } else {
      delete clonedParams.fieldType
    }

    return clonedParams
  }, [])

  const updateParamsAfterRegionClicked = useCallback((params: GetRaceListParams, clickedFilter: RegionOptionFilter) => {
    const clonedParams = { ...params, page: 1 }
    
    if (clickedFilter.name !== 'All') {
      if (clickedFilter.name === STRING_REGION.Tokyo) {
        clonedParams.region = STRING_REGION.TOKYO
      } else {
        clonedParams.region = STRING_REGION.SEOUL
      }
    } else {
      delete clonedParams.region
    }

    return clonedParams
  }, [])

  const updateParamsAfterRaceClicked = useCallback((params: GetRaceListParams, clickedFilter: RaceFilters) => {
    const clonedParams = { ...params, page: 1 }
    
    if (clickedFilter.name !== 'Default') {
      if (clickedFilter.name === RaceOption.NORMAL_RACE) {
        clonedParams.privateRace = 'false'
      } else {
        clonedParams.privateRace = 'true'
      }
    } else {
      delete clonedParams.privateRace
    }

    return clonedParams
  }, [])

  const handleClassFilterClicked = useCallback(
    (filterName: string) => () => {
      const clickedFilter = classFilters.find(filter => filter.name === filterName)

      if (!clickedFilter) return

      const newClassFilters = updateClassFilters(classFilters, clickedFilter)
      const newParams = updateParamsAfterFilterClicked(params, clickedFilter)
      setRaces([])
      setClassFilters(newClassFilters)
      setParams(newParams)
    },
    [classFilters, params]
  )

  const handleTypeFilterClicked = useCallback(
    (filterName: string) => () => {
      const clickedFilter = typeFilters.find(filter => filter.name === filterName)

      if (!clickedFilter) return

      const newTypeFilters = updateTypeFilters(typeFilters, clickedFilter)
      const newParams = updateParamsAfterTypeClicked(params, clickedFilter)

      setRaces([])
      setTypeFilters(newTypeFilters)
      setParams(newParams)
    },
    [classFilters, params]
  )

  const handleRegionFilterClicked = useCallback(
    (filterName: string) => () => {
      const clickedFilter = regionFilters.find(filter => filter.name === filterName)

      if (!clickedFilter) return

      const newRegionFilters = updateRegionFilters(regionFilters, clickedFilter)
      const newParams = updateParamsAfterRegionClicked(params, clickedFilter)

      setRaces([])
      setRegionFilters(newRegionFilters)
      setParams(newParams)
    },
    [regionFilters, params]
  )

  const handleRaceFilterClicked = useCallback(
    (filterName: string) => () => {
      const clickedFilter = raceFilters.find(filter => filter.name === filterName)

      if (!clickedFilter) return

      const newRaceFilters = updateRaceFilters(raceFilters, clickedFilter)
      const newParams = updateParamsAfterRaceClicked(params, clickedFilter)

      setRaces([])
      setRaceFilters(newRaceFilters)
      setParams(newParams)
    },
    [raceFilters, params]
  )

  const handleSearchValueChanged = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value)
  }

  const renderQueries = (query : any) => {
    let index = NUMBER.ZERO;
    let queryText = "";
    for (const property in query) {
      if (query[property]) {
        queryText += `${index ? "&" : "?"}${property}=${query[property]}`;
        index++;
      }
    }
    return queryText;
  }

  const clonedRates = useMemo(() => {
    return races.reduce((accumulator: RecordRace[], race: RecordRace) => {
      const clonedRace = { 
        ...race, 
        course: { ...race.course, name: shortenRaceCourseName(race.course.name) } 
      }
      if (!clonedRace.name.includes("   ")) {
        accumulator.push(clonedRace)
      }
      return accumulator
    }, [])
  }, [races])

  const handleSocketChange = (message: { body: string }) => {
    const { data } = JSON.parse(message.body)
    const { newStatus } = data[Object.keys(data)[NUMBER.ZERO]]
    if ([STRING_STATUS.SCHEDULING, STRING_STATUS.CANCEL].includes(newStatus)) {
      const raceUpdates = Object.values(data) as { raceId: number; newStatus: STRING_STATUS }[]
      const raceIds = raceUpdates.map((item) => item.raceId)
      setRaces(prev => prev.filter(race => !raceIds.includes(race.id)))
    }
    if ([STRING_STATUS.OPEN].includes(newStatus)) {
      document.body.scrollTop = NUMBER.ZERO
      document.documentElement.scrollTop = NUMBER.ZERO
      setParams({ ...params, page: NUMBER.ONE })
      // setRaces([])
      isRefresh = true
    }
  }

  useEffect(() => {
    if (String(defaultParams?.status) === STRING_STATUS.OPEN &&
      statusRef.current &&
      getWeatherSelector?.weather?.code !== tmpGetWeatherSelectorRef?.current?.code
    ) {
      document.body.scrollTop = NUMBER.ZERO
      document.documentElement.scrollTop = NUMBER.ZERO
      setParams({ ...params, page: NUMBER.ONE })
      setRaces([])
      isRefresh = true
    }
    tmpGetWeatherSelectorRef.current = getWeatherSelector?.weather
  }, [getWeatherSelector])

  return (
    <OpenStyled>
      <div className='head-container'>
        <OneLineTitle text={t(`${NOTIFICATION_MESSAGE}.openRaces`)} customClass='title' />
      </div>
      <div className='search-container d-flex flex-column flex-lg-row align-items-md-start' id='#top'>
        <SearchInput searchValue={searchValue} customClass='search-input' handleSearchValueChanged={handleSearchValueChanged} />
        <div className='d-flex flex-wrap align-items-center gap-4 dropdown'>
          <Select
            dataSelect={raceFilters}
            nameSelect={raceSelect}
            onSelected={(filterName: string) => handleRaceFilterClicked(filterName)()}
          />
          <Select
            dataSelect={regionFilters}
            nameSelect={regionSelect}
            onSelected={(filterName: string) => handleRegionFilterClicked(filterName)()}
          />
          <Select
            dataSelect={classFilters}
            nameSelect={classSelect}
            onSelected={(filterName: string) => handleClassFilterClicked(filterName)()}
          />
          <Select
            dataSelect={typeFilters}
            nameSelect={typeSelect}
            onSelected={(filterName: string) => handleTypeFilterClicked(filterName)()}
          />
        </div>
        <SwitchBtn title='FREE' isOn={isFreeOn} handleSwitchBtnClicked={toggleIsFreeOn} disabled={isFetching} />
        {auth.isLogged && (
          <SwitchBtn
            title={t(`${NOTIFICATION_MESSAGE}.myHorse`)}
            isOn={isMyHorseOn}
            handleSwitchBtnClicked={toggleIsMyHorseOn}
            refSwitch={useRefSwitch}
            disabled={isFetching}
          />
        )}
      </div>
      <div className='content-container'>
        <RaceTable
          columns={memoizedOpenListColumns}
          data={clonedRates ?? []}
          isRowClickable
          raisePage={setParams}
          loader={loader}
          params={params}
          paramsFilterResult={paramsFilterResult}
          totalRace={totalRace}
        />
      </div>
    </OpenStyled>
  )
}

export default Open
